<template>
  <div>
    <exo-table :root="_self" :formWidth="1300">
      <exo-form :root="_self">
        <v-row no-gutters>
          <v-col cols="6">
            <exo-input-name :root="_self" />
            <exo-input-select :root="_self" name="type">
              <exo-help-dialog title="Shape Definition">
                <ul>
                  <li>
                    <b>Sphere</b> : a basic shape to perform propagation without
                    simulating the system nor the attitude. Only the cross
                    section must be specified in order to evaluate the drag and
                    solar radiation pressure perturbations.
                  </li>
                  <br />
                  <li>
                    <b>Box</b> : define a box-shaped spacecraft (i.e. a
                    parallelepiped) by specifying its width, height and depth.
                    This shape is used to define solar arrays and to simulate
                    the spacecraft system and attitude.
                  </li>
                </ul>
              </exo-help-dialog>
            </exo-input-select>
            <exo-input-double :root="_self" name="sphericalCrossSection">
              <exo-help-dialog title="Spherical cross section">
                It can be estimated from in the simulation mission
                <i>Maneuvering Strategy Design</i>.
                <br />
              </exo-help-dialog>
            </exo-input-double>
            <v-row no-gutters justify="start">
              <v-col cols="3" class="mr-5">
                <exo-input-double
                  :root="_self"
                  name="x"
                  tooltip="x is the length along the X axis of the satellite frame"
                />
              </v-col>
              <v-col cols="3" class="mr-5">
                <exo-input-double
                  :root="_self"
                  name="y"
                  tooltip="y is the length along the Y axis of the satellite frame"
                />
              </v-col>
              <v-col cols="3">
                <exo-input-double
                  :root="_self"
                  name="z"
                  tooltip="z is the length along the Z axis of the satellite frame"
                />
              </v-col>
            </v-row>
            <br />
            <exo-card-form v-if="isBoxShape" label="Inertia">
              <template v-slot:titleTooltip>
                <exo-help-dialog margin="ml-5" title="Inertia parameters">
                  Inertia parameter of the satellite.
                  <br />
                  Here, the position of the inertial centre and the inertia
                  matrix are defined.
                  <br />
                  These parameters are only used in case of an attitude and
                  system simulation.
                  <br />
                  They are not required. In case these parameters are not
                  defined, they are automatically computed from the spacecraft
                  mass and shape, assuming an homogeneous mass repartition.
                </exo-help-dialog>
              </template>
              <v-row class="d-block" no-gutters>
                <v-col>
                  <span class="label ml-4"
                    >Inertial centre position in satellite frame.
                  </span>
                </v-col>
              </v-row>
              <v-row no-gutters justify="start">
                <v-col cols="3" class="mr-5">
                  <exo-input-double
                    :root="_self"
                    name="inertialCenterInSpacecraftFrameX"
                  />
                </v-col>
                <v-col cols="3" class="mr-5">
                  <exo-input-double
                    :root="_self"
                    name="inertialCenterInSpacecraftFrameY"
                  />
                </v-col>
                <v-col cols="3">
                  <exo-input-double
                    :root="_self"
                    name="inertialCenterInSpacecraftFrameZ"
                  />
                </v-col>
              </v-row>
              <v-row class="d-block" no-gutters>
                <v-col>
                  <span class="label ml-4"
                    >Inertial matrix at inertial centre.
                  </span>
                </v-col>
              </v-row>
              <v-row no-gutters justify="start">
                <v-col cols="3" class="mr-5">
                  <exo-input-double :root="_self" name="inertiaXX" />
                </v-col>
                <v-col cols="3" class="mr-5">
                  <exo-input-double :root="_self" name="inertiaXY" />
                </v-col>
                <v-col cols="3">
                  <exo-input-double :root="_self" name="inertiaXZ" />
                </v-col>
              </v-row>
              <v-row no-gutters justify="start">
                <v-col cols="3" class="mr-5">
                  <exo-input-double :root="_self" name="inertiaYX" />
                </v-col>
                <v-col cols="3" class="mr-5">
                  <exo-input-double :root="_self" name="inertiaYY" />
                </v-col>
                <v-col cols="3">
                  <exo-input-double :root="_self" name="inertiaYZ" />
                </v-col>
              </v-row>
              <v-row no-gutters justify="start">
                <v-col cols="3" class="mr-5">
                  <exo-input-double :root="_self" name="inertiaZX" />
                </v-col>
                <v-col cols="3" class="mr-5">
                  <exo-input-double :root="_self" name="inertiaZY" />
                </v-col>
                <v-col cols="3">
                  <exo-input-double :root="_self" name="inertiaZZ" />
                </v-col>
              </v-row>
            </exo-card-form>
            <exo-card-form v-if="isBoxShape" label="Thruster direction">
              <template v-slot:titleTooltip>
                <exo-help-dialog margin="ml-5" title="Thruster axis">
                  <p>
                    The axis of the thruster is given in the satellite body
                    frame. It represents the direction in which the thruster
                    ejects mass to modify the acceleration of the satellite.
                  </p>
                </exo-help-dialog>
              </template>
              <v-row class="d-block" no-gutters>
                <v-col>
                  <span class="label ml-4"
                    >Thruster axis in the satellite frame (opposite to thrust
                    acceleration direction)
                  </span>
                </v-col>
              </v-row>
              <v-row no-gutters justify="start">
                <v-col cols="3" class="mr-5">
                  <exo-input-double
                    :root="_self"
                    name="thrusterAxisInSatelliteFrameX"
                  />
                </v-col>
                <v-col cols="3" class="mr-5">
                  <exo-input-double
                    :root="_self"
                    name="thrusterAxisInSatelliteFrameY"
                  />
                </v-col>
                <v-col cols="3">
                  <exo-input-double
                    :root="_self"
                    name="thrusterAxisInSatelliteFrameZ"
                  />
                </v-col>
              </v-row>
            </exo-card-form>
            <exo-input-checkbox
              :root="_self"
              name="pointingOperationSystemDefined"
            >
              <exo-help-dialog
                margin="mt-0 ml-2"
                title="Pointing directions of operation system"
              >
                The operation system allows to compute the contribution of
                operation (energy consumption and attitude modification) of a
                payload and/or an antenna to the overall power consumtion of the
                battery. Their direction will be used for every mission
                supporting propagation with attitudes.

                <br />
                <br />
                The antenna allows the communication between the spacecraft and
                ground stations, when the last ones are visible.
                <br />
                The payload will operate when the spacecraft enters into an
                interest zone. During this time the spacecraft will be in NADIR
                pointing.
              </exo-help-dialog>
            </exo-input-checkbox>

            <div v-if="inputs.pointingOperationSystemDefined">
              <exo-card-form v-if="isBoxShape" label="Antenna orientation">
                <template v-slot:titleTooltip>
                  <exo-help-dialog
                    margin="ml-5"
                    title="Telecommunication antenna pointing axis"
                  >
                    Antenna pointing axis.
                    <br />

                    This direction is used in missions where the operational
                    system model is defined (see spacecraft definition). The
                    spacecraft will point towards the ground stations when such
                    mode is activated.
                    <br />
                  </exo-help-dialog>
                </template>
                <v-row class="d-block" no-gutters>
                  <v-col>
                    <span class="label ml-4"
                      >Telecommunication antenna axis in satellite frame
                    </span>
                  </v-col>
                </v-row>
                <v-row no-gutters justify="start">
                  <v-col cols="3" class="mr-5">
                    <exo-input-double
                      :root="_self"
                      name="antennaPointingAxisInSatelliteFrameX"
                    />
                  </v-col>
                  <v-col cols="3" class="mr-5">
                    <exo-input-double
                      :root="_self"
                      name="antennaPointingAxisInSatelliteFrameY"
                    />
                  </v-col>
                  <v-col cols="3">
                    <exo-input-double
                      :root="_self"
                      name="antennaPointingAxisInSatelliteFrameZ"
                    />
                  </v-col>
                </v-row>
              </exo-card-form>
              <exo-card-form v-if="isBoxShape" label="Payload pointing axis">
                <template v-slot:titleTooltip>
                  <exo-help-dialog margin="ml-5" title="Payload pointing axis">
                    Payload pointing axis.
                    <br />

                    This direction is used in missions where the operational
                    system model is defined (see spacecraft definition). The
                    spacecraft will point NADIR when this mode is activated.
                    <br />
                  </exo-help-dialog>
                </template>
                <v-row class="d-block">
                  <v-row no-gutters>
                    <span class="label ml-4"
                      >Payload pointing axis in satellite frame
                    </span>
                  </v-row>
                </v-row>
                <v-row no-gutters justify="start">
                  <v-col cols="3" class="mr-5">
                    <exo-input-double
                      :root="_self"
                      name="payloadPointingAxisInSatelliteFrameX"
                    />
                  </v-col>
                  <v-col cols="3" class="mr-5">
                    <exo-input-double
                      :root="_self"
                      name="payloadPointingAxisInSatelliteFrameY"
                    />
                  </v-col>
                  <v-col cols="3">
                    <exo-input-double
                      :root="_self"
                      name="payloadPointingAxisInSatelliteFrameZ"
                    />
                  </v-col>
                </v-row>
              </exo-card-form>
            </div>
          </v-col>

          <exo-3d-viewer v-if="isBoxShape" :root="_self"></exo-3d-viewer>
        </v-row>
        <exo-card-form v-if="isBoxShape" label="Solar array">
          <v-row no-gutters>
            <v-col cols="6">
              <exo-input-select :root="_self" name="solarArrayType">
                <exo-help-dialog title="Solar Array">
                  The power generation subsystem consists of solar arrays which
                  can be of two different kinds: body mounted or deployable.
                  Deployable solar panels can be rotating or fixed. The power
                  generated by a solar array is computed according to the
                  following relationship:
                  <br />
                  <br />
                  <katex-element
                    expression="P = F \epsilon S\cos(\delta) = P_{max} \cos(\delta)"
                  />
                  <br />
                  where
                  <br />
                  <katex-element expression="P_{max} = F \epsilon S" />
                  is the maximum power achievable with given surface and
                  efficiency values.
                  <br />
                  <katex-element expression="F = 1362 \ Wm^{-2}" />
                  is the solar constant
                  <br />
                  <katex-element expression="S" />
                  is the solar array surface
                  <br />
                  <katex-element expression="\epsilon" />
                  is the solar array efficiency (~ 0.3)
                  <br />
                  <katex-element expression="\delta" />
                  is the pointing error of the normal direction with respect to
                  the Sun
                  <br />
                  <br />
                  <b>Body mounted solar array</b>
                  <br />
                  To define a body mounted solar array, the user can specify the
                  faces of the satellite on which it will be mounted. The
                  surface of the panel will be equal to the surface of the face
                  of the satellite and the normal direction given by the normal
                  to the face. The maximum power can then be computed for a
                  given efficiency value (30% by default).
                  <br />
                  <br />
                  <v-img
                    contain
                    :src="
                      require('../../assets/images/tooltip-solar-panel-body.png')
                    "
                    aspect-ratio="3"
                  />
                  <br />
                  <br />
                  <b>Deployable solar array</b>
                  <ul>
                    <li>
                      Rotating: the user must specify the solar array axis of
                      rotation in the satellite frame. The normal vector is
                      computed at a given date in order to optimise the panel’s
                      enlightenment.
                    </li>
                    <li>
                      Fixed: the user must specify the normal direction to the
                      solar array in the satellite frame.
                    </li>
                    <br />
                    <b>Note</b
                    >: when specifying the solar arrays via
                    <i>Maximum power and efficiency</i>
                    (<katex-element expression="P_{max}, \epsilon" />) the
                    efficiency is used to compute the panel surface needed to
                    have a (maximum) instantaneous power
                    <katex-element expression="P = P_{max}" />.
                  </ul>
                  <br />
                  <br />
                  <v-img
                    contain
                    :src="
                      require('../../assets/images/tooltip-solar-panel-deployable-rotating.png')
                    "
                    aspect-ratio="3"
                  />
                  <br />
                  <br />
                  <v-img
                    contain
                    :src="
                      require('../../assets/images/tooltip-solar-panel-deployable-fixed.png')
                    "
                    aspect-ratio="3"
                  />
                  <br />
                </exo-help-dialog>
              </exo-input-select>
              <exo-input-select
                :root="_self"
                :multiple="true"
                v-if="isBodyType"
                name="bodySolarArrayFaces"
              />
              <v-row no-gutters v-if="isDeployableRotatingType" class="d-block">
                <v-row no-gutters>
                  <v-col cols="11">
                    <span class="label">Axis in satellite frame </span>
                  </v-col>
                  <v-col cols="1">
                    <exo-help-dialog
                      margin="mt-0 ml-2"
                      title="Solar Array Axis"
                    >
                      This is the solar array's axis expressed in satellite
                      frame.
                      <br />
                      This is the axis of the solar array. For a rotating solar
                      panel, it is the same as the rotation axis.
                    </exo-help-dialog>
                  </v-col>
                </v-row>
                <v-row no-gutters justify="start">
                  <v-col cols="3" class="mr-5">
                    <exo-input-double
                      :root="_self"
                      name="solarArrayAxisInSatelliteFrameX"
                    />
                  </v-col>
                  <v-col cols="3" class="mr-5">
                    <exo-input-double
                      :root="_self"
                      name="solarArrayAxisInSatelliteFrameY"
                    />
                  </v-col>
                  <v-col cols="3">
                    <exo-input-double
                      :root="_self"
                      name="solarArrayAxisInSatelliteFrameZ"
                    />
                  </v-col>
                </v-row>
              </v-row>
              <v-row no-gutters v-if="isDeployableFixedType" class="d-block">
                <v-row no-gutters>
                  <v-col cols="11">
                    <span class="label">Normal in satellite frame </span>
                  </v-col>
                  <v-col cols="1">
                    <exo-help-dialog
                      margin="mt-0 ml-2"
                      title="Solar Array Normal"
                    >
                      The normal direction to the solar array, expressed in
                      spacecraft frame. This direction needs to be specified for
                      a deployable non rotating panel. It is the direction which
                      will be pointed towards the Sun when the spacecraft is in
                      <strong>recharge</strong> mode. <br />
                      <br />
                    </exo-help-dialog>
                  </v-col>
                </v-row>
                <v-row no-gutters justify="start">
                  <v-col cols="3" class="mr-5">
                    <exo-input-double
                      :root="_self"
                      name="solarArrayNormalInSatelliteFrameX"
                    />
                  </v-col>
                  <v-col cols="3" class="mr-5">
                    <exo-input-double
                      :root="_self"
                      name="solarArrayNormalInSatelliteFrameY"
                    />
                  </v-col>
                  <v-col cols="3">
                    <exo-input-double
                      :root="_self"
                      name="solarArrayNormalInSatelliteFrameZ"
                    />
                  </v-col>
                </v-row>
              </v-row>
              <exo-input-select :root="_self" name="solarArrayDefinitionType" />
              <exo-input-double :root="_self" name="solarArrayMaximumPower" />
              <exo-input-double :root="_self" name="solarArraySurface" />
              <exo-input-double :root="_self" name="solarArrayEfficiency" />
            </v-col>
            <v-col md1 />
            <v-col cols="5">
              <div v-if="isBodyType">
                <b class="grey--text">Body mounted solar array</b>
                <br />
                To define a body mounted solar array, the user can specify the
                faces of the satellite on which it will be mounted. The surface
                of the panel will be equal to the surface of the face of the
                satellite and the normal direction given by the normal to the
                face. The maximum power can then be computed for a given
                efficiency value (30% by default).
                <br />
                <br />
                <v-img
                  contain
                  :src="
                    require('../../assets/images/tooltip-solar-panel-body.png')
                  "
                  aspect-ratio="1.8"
                />
              </div>
              <div v-if="isDeployableRotatingType">
                <b class="grey--text">Rotating deployable solar array</b>
                <br />
                The user must specify the solar array axis of rotation in the
                satellite frame. The normal vector is computed at a given date
                in order to optimise the panel’s enlightenment.
                <br />
                <br />
                <v-img
                  contain
                  :src="
                    require('../../assets/images/tooltip-solar-panel-deployable-rotating.png')
                  "
                  aspect-ratio="1.8"
                />
              </div>
              <div v-if="isDeployableFixedType">
                <b class="grey--text">Fixed deployable solar array</b>
                <br />
                The user must specify the normal direction to the solar array in
                the satellite frame.
                <br />
                <br />
                <v-img
                  contain
                  :src="
                    require('../../assets/images/tooltip-solar-panel-deployable-fixed.png')
                  "
                  aspect-ratio="1.8"
                />
              </div>
            </v-col>
          </v-row>
        </exo-card-form>
      </exo-form>
    </exo-table>
    <exo-modal-references :root="_self" :modalContent="modalRef" />
  </div>
</template>

<script>
import formMixin from '../../mixins/forms.js'
import numericalMixin from '../../mixins/numericalForms.js'
import fieldsDescription from '../../tools/fieldsDescription.json'
import dataTools from '../../tools/data.js'

export default {
  // 3D MODEL

  // 3D MODEL ABOVE IN TESTING

  name: 'spacecraftGeometry',

  mixins: [formMixin, numericalMixin],

  computed: {
    isBoxShape() {
      return this.inputs.type === 'BOX'
    },
    isBodyType() {
      return (
        this.isBoxShape &&
        this.inputs.solarArrayType === 'SOLAR_ARRAY_TYPE_BODY'
      )
    },
    isDeployableType() {
      return this.isDeployableRotatingType || this.isDeployableFixedType
    },
    isDeployableRotatingType() {
      return (
        this.isBoxShape &&
        this.inputs.solarArrayType === 'SOLAR_ARRAY_TYPE_DEPLOYABLE_ROTATING'
      )
    },
    isDeployableFixedType() {
      return (
        this.isBoxShape &&
        this.inputs.solarArrayType === 'SOLAR_ARRAY_TYPE_DEPLOYABLE_FIXED'
      )
    }
  },

  data() {
    return {
      tableFields: [
        { value: 'displayedName', text: 'Name', sortable: true },
        { value: 'type', text: 'Type', sortable: true },
        {
          value: 'x',
          text: 'X' + this.getDefaultUnitLabel('x'),
          sortable: true
        },
        {
          value: 'y',
          text: 'Y' + this.getDefaultUnitLabel('y'),
          sortable: true
        },
        {
          value: 'z',
          text: 'Z' + this.getDefaultUnitLabel('z'),
          sortable: true
        },
        { value: 'solarArrayType', text: 'Solar array', sortable: true },
        {
          value: 'solarArrayMaximumPower',
          text:
            'Maximum power' +
            this.getDefaultUnitLabel('solarArrayMaximumPower'),
          sortable: true
        },
        {
          value: 'solarArraySurface',
          text:
            'Solar array surface' +
            this.getDefaultUnitLabel('solarArraySurface'),
          sortable: true
        },
        {
          value: 'bodySolarArrayFaces',
          text: 'Solar array faces',
          sortable: true
        },
        { value: 'actions', text: 'Actions', sortable: false }
      ],

      modalRef: { content: [] },
      showModal: false,

      enumPropertiesList: this.getEnumProperties(),

      inputs: {
        type: this.getDefaultValue('type'),
        x: this.getDefaultValue('x'),
        y: this.getDefaultValue('y'),
        z: this.getDefaultValue('z'),
        pointingOperationSystemDefined: this.getDefaultValue(
          'pointingOperationSystemDefined'
        ),
        solarArrayAxisInSatelliteFrameX: this.getDefaultValue(
          'solarArrayAxisInSatelliteFrameX'
        ),
        solarArrayAxisInSatelliteFrameY: this.getDefaultValue(
          'solarArrayAxisInSatelliteFrameY'
        ),
        solarArrayAxisInSatelliteFrameZ: this.getDefaultValue(
          'solarArrayAxisInSatelliteFrameZ'
        ),
        inertialCenterInSpacecraftFrameX: this.getDefaultValue(
          'inertialCenterInSpacecraftFrameX'
        ),
        inertialCenterInSpacecraftFrameY: this.getDefaultValue(
          'inertialCenterInSpacecraftFrameY'
        ),
        inertialCenterInSpacecraftFrameZ: this.getDefaultValue(
          'inertialCenterInSpacecraftFrameZ'
        ),
        inertiaXX: this.getDefaultValue('inertiaXX'),
        inertiaXY: this.getDefaultValue('inertiaXY'),
        inertiaXZ: this.getDefaultValue('inertiaXZ'),
        inertiaYX: this.getDefaultValue('inertiaYX'),
        inertiaYY: this.getDefaultValue('inertiaYY'),
        inertiaYZ: this.getDefaultValue('inertiaYZ'),
        inertiaZX: this.getDefaultValue('inertiaZX'),
        inertiaZY: this.getDefaultValue('inertiaZY'),
        inertiaZZ: this.getDefaultValue('inertiaZZ'),
        solarArrayNormalInSatelliteFrameX: this.getDefaultValue(
          'solarArrayNormalInSatelliteFrameX'
        ),
        solarArrayNormalInSatelliteFrameY: this.getDefaultValue(
          'solarArrayNormalInSatelliteFrameY'
        ),
        solarArrayNormalInSatelliteFrameZ: this.getDefaultValue(
          'solarArrayNormalInSatelliteFrameZ'
        ),
        solarArrayDefinitionType: this.getDefaultValue(
          'solarArrayDefinitionType'
        ),
        solarArrayType: this.getDefaultValue('solarArrayType'),
        bodySolarArrayFaces: this.getDefaultValue('bodySolarArrayFaces'),
        solarArrayEfficiency: this.getDefaultValue('solarArrayEfficiency'),
        solarArrayMaximumPower: this.getDefaultValue('solarArrayMaximumPower'),
        solarArraySurface: this.getDefaultValue('solarArraySurface'),
        thrusterAxisInSatelliteFrameX: this.getDefaultValue(
          'thrusterAxisInSatelliteFrameX'
        ),
        thrusterAxisInSatelliteFrameY: this.getDefaultValue(
          'thrusterAxisInSatelliteFrameY'
        ),
        thrusterAxisInSatelliteFrameZ: this.getDefaultValue(
          'thrusterAxisInSatelliteFrameZ'
        ),
        payloadPointingAxisInSatelliteFrameX: this.getDefaultValue(
          'payloadPointingAxisInSatelliteFrameX'
        ),
        payloadPointingAxisInSatelliteFrameY: this.getDefaultValue(
          'payloadPointingAxisInSatelliteFrameY'
        ),
        payloadPointingAxisInSatelliteFrameZ: this.getDefaultValue(
          'payloadPointingAxisInSatelliteFrameZ'
        ),
        antennaPointingAxisInSatelliteFrameX: this.getDefaultValue(
          'antennaPointingAxisInSatelliteFrameX'
        ),
        antennaPointingAxisInSatelliteFrameY: this.getDefaultValue(
          'antennaPointingAxisInSatelliteFrameY'
        ),
        antennaPointingAxisInSatelliteFrameZ: this.getDefaultValue(
          'antennaPointingAxisInSatelliteFrameZ'
        ),
        sphericalCrossSection: this.getDefaultValue('sphericalCrossSection'),
        scope: this.getDefaultValue('scope')
      }
    }
  },

  methods: {
    getCurrentFormType() {
      return 'SpacecraftGeometryDefinition'
    },

    getRequiredPropertiesList(instance) {
      let defType = this.getDefinitionType(instance)
      let objectType = this.getCurrentFormType()
      let requiredList = dataTools.getPropertiesList(
        fieldsDescription[objectType],
        defType,
        'required'
      )
      if (this.inputs.pointingOperationSystemDefined) {
        requiredList.push('payloadPointingAxisInSatelliteFrameX')
        requiredList.push('payloadPointingAxisInSatelliteFrameY')
        requiredList.push('payloadPointingAxisInSatelliteFrameZ')
        requiredList.push('antennaPointingAxisInSatelliteFrameX')
        requiredList.push('antennaPointingAxisInSatelliteFrameY')
        requiredList.push('antennaPointingAxisInSatelliteFrameZ')
      }
      return requiredList
    },

    getOptionalPropertiesList(instance) {
      let defType = this.getDefinitionType(instance)
      let objectType = this.getCurrentFormType()
      return dataTools.getPropertiesList(
        fieldsDescription[objectType],
        defType,
        'optional'
      )
    },

    getAllPropertiesUnionList() {
      let objectType = this.getCurrentFormType()
      let requiredList = dataTools.getPropertiesList(
        fieldsDescription[objectType],
        null,
        'required'
      )
      let optionalList = dataTools.getPropertiesList(
        fieldsDescription[objectType],
        null,
        'optional'
      )
      return [...new Set(requiredList.concat(optionalList))]
    },

    getDefinitionType() {
      let elements = { type: this.inputs.type }
      if (this.isBoxShape) {
        elements.solarArrayType = this.inputs.solarArrayType
        if (this.isDeployableType) {
          elements.solarArrayDefinitionType =
            this.inputs.solarArrayDefinitionType
        }
      }
      return elements
    },

    getRefList(param) {
      return []
    }
  }
}
</script>
